import React, { useState } from 'react';

interface Player {
  name: string;
  era: string;
  pos: string;
  ppg: number;
  rpg: number;
  apg: number;
  championships: number;
  finals: number;
  goatScore: number;
  color: string;
}

const PLAYERS: Player[] = [
  { name: 'Michael Jordan',    era: '1984–2003', pos: 'SG', ppg: 31.4, rpg: 6.2,  apg: 5.3, championships: 6,  finals: 6,  goatScore: 96.2, color: '#F5820A' },
  { name: 'LeBron James',      era: '2003–pres', pos: 'SF', ppg: 28.7, rpg: 7.5,  apg: 7.3, championships: 4,  finals: 10, goatScore: 93.8, color: '#7B8FD4' },
  { name: 'Kareem Abdul-Jabbar',era: '1969–1989', pos: 'C',  ppg: 29.2, rpg: 11.2, apg: 3.6, championships: 6,  finals: 10, goatScore: 91.4, color: '#2ECC71' },
  { name: 'Magic Johnson',     era: '1979–1996', pos: 'PG', ppg: 22.0, rpg: 7.2,  apg: 11.2,championships: 5,  finals: 9,  goatScore: 89.7, color: '#C9A84C' },
  { name: 'Bill Russell',      era: '1956–1969', pos: 'C',  ppg: 15.1, rpg: 22.5, apg: 4.3, championships: 11, finals: 12, goatScore: 88.9, color: '#E74C3C' },
  { name: 'Wilt Chamberlain',  era: '1959–1973', pos: 'C',  ppg: 30.1, rpg: 22.9, apg: 4.4, championships: 2,  finals: 6,  goatScore: 87.5, color: '#9B59B6' },
  { name: 'Larry Bird',        era: '1979–1992', pos: 'SF', ppg: 24.3, rpg: 10.0, apg: 6.3, championships: 3,  finals: 5,  goatScore: 86.1, color: '#3498DB' },
  { name: 'Kobe Bryant',       era: '1996–2016', pos: 'SG', ppg: 25.0, rpg: 5.2,  apg: 4.7, championships: 5,  finals: 7,  goatScore: 85.8, color: '#8E44AD' },
];

const METRICS = ['ppg', 'rpg', 'apg', 'championships', 'goatScore'];
const METRIC_LABELS: Record<string, string> = {
  ppg: 'PPG', rpg: 'RPG', apg: 'APG',
  championships: 'Rings', goatScore: 'GOAT Index'
};

const API_URL = process.env.REACT_APP_API_URL;

const GOATMode: React.FC = () => {
  const [selected, setSelected]     = useState<string[]>(['Michael Jordan', 'LeBron James']);
  const [analysis, setAnalysis]     = useState('');
  const [loading, setLoading]       = useState(false);
  const [activeMetric, setMetric]   = useState('goatScore');

  const togglePlayer = (name: string) => {
    setSelected(prev =>
      prev.includes(name)
        ? prev.filter(p => p !== name)
        : prev.length < 3 ? [...prev, name] : prev
    );
  };

  const selectedPlayers = PLAYERS.filter(p => selected.includes(p.name));

  const maxVal = (metric: string) =>
    Math.max(...PLAYERS.map(p => p[metric as keyof Player] as number));

  const runGOATAnalysis = async () => {
    if (selected.length < 2) return;
    setLoading(true);
    setAnalysis('');
    try {
      const res = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: `GOAT Mode Analysis: Compare ${selected.join(' vs ')} across all dimensions — era-adjusted scoring, championships, advanced metrics, and legacy. Give a definitive GOAT ranking with justification.`
        })
      });
      const data = await res.json();
      setAnalysis(data.response || data.error);
    } catch {
      setAnalysis('Connection error. Check your API endpoint.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: '#0D0F14', minHeight: 'calc(100vh - 48px)', color: '#F0EDE6', padding: '20px 24px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 22, color: '#F5820A', margin: 0 }}>GOAT Mode</h1>
          <p style={{ fontFamily: 'sans-serif', fontSize: 12, color: '#8892A4', margin: '4px 0 0' }}>
            Select up to 3 players to compare. Click Analyze for AI-powered verdict.
          </p>
        </div>
        <button onClick={runGOATAnalysis} disabled={loading || selected.length < 2} style={{
          background: loading ? '#333' : '#F5820A',
          border: 'none', borderRadius: 8, padding: '10px 20px',
          color: '#fff', fontFamily: 'Courier New', fontSize: 11,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          cursor: loading || selected.length < 2 ? 'not-allowed' : 'pointer'
        }}>
          {loading ? 'Analyzing...' : '◉ Run GOAT Analysis'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* LEFT — Player selector + bar chart */}
        <div>
          {/* Metric selector */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {METRICS.map(m => (
              <button key={m} onClick={() => setMetric(m)} style={{
                fontFamily: 'Courier New', fontSize: 9, padding: '4px 10px',
                borderRadius: 20, border: `0.5px solid ${activeMetric === m ? '#F5820A' : 'rgba(255,255,255,0.15)'}`,
                background: activeMetric === m ? 'rgba(245,130,10,0.12)' : 'transparent',
                color: activeMetric === m ? '#F5820A' : '#8892A4',
                cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em'
              }}>{METRIC_LABELS[m]}</button>
            ))}
          </div>

          {/* Bar chart */}
          <div style={{
            background: '#13161D', border: '0.5px solid rgba(255,255,255,0.08)',
            borderRadius: 10, padding: '16px', marginBottom: 16
          }}>
            {PLAYERS.map(p => {
              const val    = p[activeMetric as keyof Player] as number;
              const max    = maxVal(activeMetric);
              const pct    = (val / max) * 100;
              const isSel  = selected.includes(p.name);
              return (
                <div key={p.name} onClick={() => togglePlayer(p.name)}
                  style={{ marginBottom: 10, cursor: 'pointer', opacity: isSel ? 1 : 0.4, transition: 'opacity 0.15s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontFamily: 'sans-serif', fontSize: 12, color: isSel ? '#F0EDE6' : '#8892A4' }}>
                      {p.name}
                    </span>
                    <span style={{ fontFamily: 'Courier New', fontSize: 11, color: p.color }}>{val}</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3 }}>
                    <div style={{
                      height: '100%', width: `${pct}%`,
                      background: isSel ? p.color : 'rgba(255,255,255,0.15)',
                      borderRadius: 3, transition: 'width 0.4s ease'
                    }} />
                  </div>
                </div>
              );
            })}
          </div>

          <p style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4', textAlign: 'center', letterSpacing: '0.08em' }}>
            CLICK PLAYERS TO SELECT · MAX 3
          </p>
        </div>

        {/* RIGHT — Comparison cards + analysis */}
        <div>
          {/* Selected player cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {selectedPlayers.map(p => (
              <div key={p.name} style={{
                background: '#13161D',
                border: `0.5px solid ${p.color}44`,
                borderLeft: `3px solid ${p.color}`,
                borderRadius: 10, padding: '12px 16px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontFamily: 'Georgia, serif', fontSize: 15, fontWeight: 700 }}>{p.name}</div>
                    <div style={{ fontFamily: 'Courier New', fontSize: 9, color: '#8892A4', marginTop: 2 }}>
                      {p.era} · {p.pos}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Courier New', fontSize: 18, color: p.color, fontWeight: 700 }}>{p.goatScore}</div>
                    <div style={{ fontFamily: 'Courier New', fontSize: 8, color: '#8892A4' }}>GOAT INDEX</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginTop: 12 }}>
                  {[
                    { label: 'PPG',   val: p.ppg },
                    { label: 'RPG',   val: p.rpg },
                    { label: 'APG',   val: p.apg },
                    { label: 'RINGS', val: p.championships },
                    { label: 'FINALS',val: p.finals }
                  ].map(s => (
                    <div key={s.label} style={{ textAlign: 'center' }}>
                      <div style={{ fontFamily: 'Courier New', fontSize: 13, color: p.color }}>{s.val}</div>
                      <div style={{ fontFamily: 'Courier New', fontSize: 8, color: '#8892A4' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* AI Analysis output */}
          {(analysis || loading) && (
            <div style={{
              background: '#13161D',
              border: '0.5px solid rgba(245,130,10,0.3)',
              borderRadius: 10, padding: 16
            }}>
              <div style={{
                fontFamily: 'Courier New', fontSize: 9, color: '#F5820A',
                letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10
              }}>
                ◉ GOAT Analysis
              </div>
              {loading
                ? <div style={{ fontFamily: 'sans-serif', fontSize: 13, color: '#8892A4' }}>
                    Analyzing {selected.join(' vs ')}...
                  </div>
                : <div style={{ fontFamily: 'sans-serif', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', color: '#F0EDE6' }}>
                    {analysis}
                  </div>
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GOATMode;